import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { IMdfActivityApi } from 'partner360/features/mdfActivities/api/types'
import { mapMdfActivityFromApi, parseSeparatedString } from 'partner360/features/mdfActivities/lib'
import { TMdfStatusCategory } from 'partner360/features/mdfActivities/model/types'

import { ICockpitMdfActivityApi } from '../api/types'
import { ICockpitMdfActivity } from '../model/types'

const STATUS_CATEGORIES: TMdfStatusCategory[] = ['Planning', 'Execution', 'Payment', 'Paid']

/** `planningCockpitGzip` also returns `''` for an unclassified activity, where the stored
 *  procedure returns `null`; anything outside the known phases reads as "not classified". */
const parseStatusCategory = (value: string | undefined): TMdfStatusCategory =>
  STATUS_CATEGORIES.find((category) => category === value) ?? null

/**
 * Maps a cockpit MDF activity onto the shared `IMdfActivity` domain model, so every MDF
 * calculation (allocation split, requested/paid sums, FX rate) is the one the `mdfActivities`
 * feature already owns instead of a cockpit-local copy.
 *
 * The gap between the two payloads is filled here rather than in the domain model:
 * `planningCockpitGzip` omits absent keys where the stored procedure sends `null`, and it does
 * not return the outcome block or `P360InitiativeId` at all — those become `null`, the same
 * value an unfilled activity carries. Nothing downstream can tell the two sources apart, which
 * is the point: the cockpit tile reuses the MDF widget's aggregations as-is.
 */
/** The offerings list arrives either as an array or as a `;`/`,`-separated string. */
const parseProgramTypes = (value: string | string[] | undefined): string[] =>
  Array.isArray(value)
    ? value.map((item) => item.trim()).filter(Boolean)
    : parseSeparatedString(value)

const OFFERINGS_BY_BUSINESS_UNIT: Record<string, readonly ProgramTypes[]> = {
  print: [ProgramTypes.CorePrint, ProgramTypes.Print],
  supplies: [ProgramTypes.CorePrint, ProgramTypes.Supplies],
  ps: [ProgramTypes.PersonalSystems, ProgramTypes.PC],
  ws: [ProgramTypes.PersonalSystems, ProgramTypes.PC],
  alliance: [ProgramTypes.PersonalSystems, ProgramTypes.PC],
  'large format': [ProgramTypes.LargeFormat, ProgramTypes.LF],
}

const inferProgramTypes = (activity: ReturnType<typeof mapMdfActivityFromApi>): string[] =>
  Array.from(
    new Set(
      [
        ...activity.businessUnit,
        ...activity.strategicInitiativeAllocations.map(({ businessUnit }) => businessUnit),
      ].flatMap(
        (businessUnit) => OFFERINGS_BY_BUSINESS_UNIT[businessUnit.trim().toLowerCase()] ?? []
      )
    )
  )

export const mapCockpitMdfActivityFromApi = (
  activity: ICockpitMdfActivityApi
): ICockpitMdfActivity => {
  const mappedActivity = mapMdfActivityFromApi({
    ...activity,
    ActivityStatus: activity.ActivityStatus ?? null,
    StatusCategory: parseStatusCategory(activity.StatusCategory),
    StrategicInitiative: activity.StrategicInitiative ?? null,
    BusinessUnit: activity.BusinessUnit ?? null,
    BusinessFocus: activity.BusinessFocus ?? null,
    Category: activity.Category ?? null,
    ApprovalDate: activity.ApprovalDate ?? null,
    KeyInitiativeId: activity.KeyInitiativeId ?? null,
    KeyInitiativeLinkType: activity.KeyInitiativeLinkType ?? null,
    P360InitiativeId: null,
    Outcome: null,
    OutcomeValueType: null,
    FunnelSize: null,
    Leads: null,
    ConversionRate: null,
    Attendance: null,
    EstimateSales: null,
  } satisfies IMdfActivityApi)
  const programTypes = parseProgramTypes(activity.ProgramTypes)

  return {
    ...mappedActivity,
    programTypes: Array.from(new Set([...programTypes, ...inferProgramTypes(mappedActivity)])),
  }
}
